import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import { heroData } from "../mock";
import SEO from "../components/SEO";

const HomePage = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(0);

  const toggleItem = (i) => {
    setOpenIndex(openIndex === i ? -1 : i);
  };

  const features = t("home.features.items", { returnObjects: true });
  const stackItems = t("home.stack.items", { returnObjects: true });
  const managedChecks = t("home.benefits.managed.checks", { returnObjects: true });
  const apiChecks = t("home.benefits.api.checks", { returnObjects: true });

  return (
    <div data-testid="home-page">
      <SEO
        path="/"
        title={t("seo.home.title")}
        description={t("seo.home.description")}
        keywords={t("seo.home.keywords")}
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Card Issuing & Processing",
          "serviceType": "Card Issuing & Processing",
          "provider": { "@type": "Organization", "name": t("brand.name") },
          "areaServed": ["Mexico", "Latin America"],
          "description": t("seo.home.description")
        }}
      />
      {/* Hero Section - Rain Style Split */}
      <section className="heroSection" style={styles.heroSection}>
        <div className="container">
          <div className="heroGrid" style={styles.heroGrid}>
            {/* Left - Card Image */}
            <div className="heroImageSide" style={styles.heroImageSide}>
              <img
                src="https://customer-assets.emergentagent.com/job_cardmex/artifacts/6tisadkm_Gemini_Generated_Image_7kd3ce7kd3ce7kd3.png"
                alt={t("home.hero.imageAlt")}
                className="heroCardImage" style={styles.heroCardImage}
              />
            </div>

            {/* Right - Text Content */}
            <div className="heroTextSide" style={styles.heroTextSide}>
              <h1 className="mainTitle" style={styles.mainTitle}>
                {t("home.hero.titleLine1")}<br />
                {t("home.hero.titleLine2")}
              </h1>
              <p className="mainSubtitle" style={styles.mainSubtitle}>
                {t("home.hero.subtitle")}
              </p>

              <Link to="/contacto">
                <button className="btn-primary primaryCta" style={styles.primaryCta} data-testid="home-hero-cta">
                  {t("common.contactUs")}
                  <ArrowRight size={20} style={{ marginLeft: "8px" }} />
                </button>
              </Link>

              {/* Stats */}
              <div className="heroStats" style={styles.heroStats}>
                <div className="statItem" style={styles.statItem}>
                  <div className="statNumber" style={styles.statNumber}>{t("home.hero.stats.weeks.number")}</div>
                  <div className="statLabel" style={styles.statLabel}>{t("home.hero.stats.weeks.label")}</div>
                </div>
                <div className="statItem" style={styles.statItem}>
                  <div className="statNumber" style={styles.statNumber}>{t("home.hero.stats.mastercard.number")}</div>
                  <div className="statLabel" style={styles.statLabel}>{t("home.hero.stats.mastercard.label")}</div>
                </div>
                <div className="statItem" style={styles.statItem}>
                  <div className="statNumber" style={styles.statNumber}>{t("home.hero.stats.noCode.number")}</div>
                  <div className="statLabel" style={styles.statLabel}>{t("home.hero.stats.noCode.label")}</div>
                </div>
                <div className="statItem" style={styles.statItem}>
                  <div className="statNumber" style={styles.statNumber}>{t("home.hero.stats.agents.number")}</div>
                  <div className="statLabel" style={styles.statLabel}>{t("home.hero.stats.agents.label")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features - Rain Style */}
      <section className="featuresSection" style={styles.featuresSection}>
        <div className="container">
          <h2 className="featuresSectionTitle" style={styles.featuresSectionTitle}>{t("home.features.title")}</h2>

          <div className="featuresGrid" style={styles.featuresGrid}>
            {features.map((f, i) => (
              <div key={i} className="feature-card featureCard" style={styles.featureCard} data-testid={`home-feature-${i}`}>
                <h3 className="feature-title featureTitle" style={styles.featureTitle}>{f.title}</h3>
                <p className="feature-description featureDescription" style={styles.featureDescription}>{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Stack Section */}
      <section className="fullStackSection" style={styles.fullStackSection}>
        <div className="container">
          <div className="fullStackHeader" style={styles.fullStackHeader}>
            <h2 className="fullStackTitle" style={styles.fullStackTitle}>{t("home.stack.title")}</h2>
            <p className="fullStackSubtitle" style={styles.fullStackSubtitle}>{t("home.stack.subtitle")}</p>
          </div>

          <div className="stackList" style={styles.stackList} data-testid="stack-accordion">
            {stackItems.map((item, i) => {
              const isOpen = openIndex === i;
              return (
                <div key={i} className="stackListItem" style={styles.stackListItem} data-testid={`stack-item-${i}`}>
                  <button
                    type="button"
                    onClick={() => toggleItem(i)}
                    className="stackListHeader" style={styles.stackListHeader}
                    aria-expanded={isOpen}
                    data-testid={`stack-item-toggle-${i}`}
                  >
                    <span className="stackListTitle" style={styles.stackListTitle}>{item.title}</span>
                    <ChevronDown
                      size={24}
                      strokeWidth={1.75}
                      style={{
                        ...styles.stackListChevron,
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        color: isOpen ? "#667eea" : "#718096"
                      }}
                    />
                  </button>
                  <div
                    style={{
                      ...styles.stackListBody,
                      maxHeight: isOpen ? "300px" : "0px",
                      opacity: isOpen ? 1 : 0,
                      paddingTop: isOpen ? "0" : "0",
                      paddingBottom: isOpen ? "28px" : "0"
                    }}
                  >
                    <p className="stackListDescription" style={styles.stackListDescription}>{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits - Rain Style */}
      <section className="benefitsSection" style={styles.benefitsSection}>
        <div className="container">
          <div className="benefitsTwoColumn" style={styles.benefitsTwoColumn}>
            {/* Left Half - Managed */}
            <div className="benefitsHalf" style={styles.benefitsHalf}>
              <h2 className="benefitsTitle" style={styles.benefitsTitle}>
                {t("home.benefits.managed.titleLine1")}<br />
                {t("home.benefits.managed.titleLine2")}
              </h2>
              <p className="benefitsDescription" style={styles.benefitsDescription}>
                {t("home.benefits.managed.description")}
              </p>

              <div className="checkList" style={styles.checkList}>
                {managedChecks.map((c, i) => (
                  <div key={i} className="checkItem" style={styles.checkItem}>
                    <Check size={20} color="#00A8E8" />
                    <span>{c}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Half - API */}
            <div className="benefitsHalf" style={styles.benefitsHalf}>
              <h2 className="benefitsTitleAlt" style={styles.benefitsTitleAlt}>
                {t("home.benefits.api.titleLine1")}<br />
                {t("home.benefits.api.titleLine2")}<br />
                {t("home.benefits.api.titleLine3")}
              </h2>
              <p className="benefitsDescription" style={styles.benefitsDescription}>
                {t("home.benefits.api.description")}
              </p>

              <div className="checkList" style={styles.checkList}>
                {apiChecks.map((c, i) => (
                  <div key={i} className="checkItem" style={styles.checkItem}>
                    <Check size={20} color="#f5576c" />
                    {i === 1 ? (
                      <a
                        href="https://caas-undostres.readme.io/reference"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="docLink" style={styles.docLink}
                        data-testid="home-doc-link"
                      >
                        {c}
                      </a>
                    ) : (
                      <span>{c}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Rain Style */}
      <section className="finalCta" style={styles.finalCta}>
        <div className="container">
          <div className="ctaContent" style={styles.ctaContent}>
            <h2 className="ctaTitle" style={styles.ctaTitle}>{t("home.finalCta.title")}</h2>
            <Link to="/contacto">
              <button className="btn-primary ctaButton" style={styles.ctaButton} data-testid="home-final-cta">
                {t("common.contactUs")}
                <ArrowRight size={20} style={{ marginLeft: "8px" }} />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  // Hero Section - Split Layout
  heroSection: {
    padding: "140px 20px 100px",
    background: "#ffffff"
  },
  heroGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "60px",
    alignItems: "center",
    maxWidth: "1400px",
    margin: "0 auto"
  },
  heroImageSide: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  heroCardImage: {
    width: "100%",
    maxWidth: "550px",
    height: "auto",
    borderRadius: "16px"
  },
  heroTextSide: {
    textAlign: "left"
  },
  mainTitle: {
    fontSize: "64px",
    fontWeight: "700",
    lineHeight: "1.1",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "28px",
    letterSpacing: "-0.03em"
  },
  mainSubtitle: {
    fontSize: "20px",
    lineHeight: "1.6",
    color: "#4a5568",
    marginBottom: "40px",
    fontWeight: "400"
  },
  primaryCta: {
    fontSize: "17px",
    padding: "16px 36px",
    marginBottom: "60px"
  },
  heroStats: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "28px",
    paddingTop: "48px",
    borderTop: "1px solid rgba(0, 0, 0, 0.1)",
    width: "100%"
  },
  statItem: {
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  statNumber: {
    fontSize: "22px",
    fontWeight: "700",
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    letterSpacing: "-0.02em",
    lineHeight: "1",
    whiteSpace: "nowrap"
  },
  statLabel: {
    fontSize: "14px",
    color: "#718096",
    fontWeight: "500",
    lineHeight: "1.4"
  },
  
  // Features
  featuresSection: {
    padding: "100px 20px",
    background: "#f8f9fc"
  },
  featuresSectionTitle: {
    fontSize: "52px",
    fontWeight: "700",
    textAlign: "center",
    marginBottom: "80px",
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    letterSpacing: "-0.02em"
  },
  featuresGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "40px",
    maxWidth: "1200px",
    margin: "0 auto"
  },
  featureCard: {
    textAlign: "left",
    padding: "40px",
    background: "#ffffff",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    borderRadius: "16px",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
  },
  
  // Full Stack Section
  fullStackSection: {
    padding: "100px 20px",
    background: "#ffffff",
    borderTop: "1px solid rgba(0, 0, 0, 0.08)"
  },
  fullStackHeader: {
    textAlign: "center",
    maxWidth: "700px",
    margin: "0 auto 60px"
  },
  fullStackTitle: {
    fontSize: "52px",
    fontWeight: "700",
    lineHeight: "1.2",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "20px",
    letterSpacing: "-0.02em"
  },
  fullStackTitleAccent: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text"
  },
  fullStackSubtitle: {
    fontSize: "19px",
    lineHeight: "1.6",
    color: "#4a5568"
  },
  fullStackGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "32px",
    maxWidth: "1200px",
    margin: "0 auto"
  },
  fullStackCard: {
    textAlign: "left",
    padding: "40px",
    background: "#ffffff",
    borderRadius: "16px",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)"
  },
  fullStackNumber: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#667eea",
    marginBottom: "20px"
  },
  fullStackCardTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1a202c",
    marginBottom: "16px",
    letterSpacing: "-0.01em"
  },
  fullStackCardDescription: {
    fontSize: "16px",
    lineHeight: "1.7",
    color: "#4a5568"
  },

  // Stack accordion list
  stackList: {
    maxWidth: "900px",
    margin: "0 auto",
    borderTop: "1px solid rgba(0, 0, 0, 0.1)"
  },
  stackListItem: {
    borderBottom: "1px solid rgba(0, 0, 0, 0.1)"
  },
  stackListHeader: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "28px 8px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
    outline: "none"
  },
  stackListTitle: {
    fontSize: "22px",
    fontWeight: "600",
    color: "#1a202c",
    letterSpacing: "-0.01em"
  },
  stackListChevron: {
    flexShrink: 0,
    transition: "transform 0.35s ease, color 0.25s ease"
  },
  stackListBody: {
    overflow: "hidden",
    paddingLeft: "8px",
    paddingRight: "44px",
    transition: "max-height 0.35s ease, opacity 0.3s ease, padding 0.3s ease"
  },
  stackListDescription: {
    fontSize: "17px",
    lineHeight: "1.7",
    color: "#4a5568",
    margin: 0
  },

  featureNumber: {
    fontSize: "16px",
    color: "#667eea",
    marginBottom: "24px",
    fontWeight: "700"
  },
  featureTitle: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1a202c",
    marginBottom: "16px",
    letterSpacing: "-0.01em"
  },
  featureDescription: {
    fontSize: "17px",
    lineHeight: "1.7",
    color: "#4a5568",
    fontWeight: "400"
  },
  
  // Benefits
  benefitsSection: {
    padding: "100px 20px",
    background: "#f8f9fc",
    borderTop: "1px solid rgba(0, 0, 0, 0.08)"
  },
  benefitsTwoColumn: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "60px",
    maxWidth: "1200px",
    margin: "0 auto"
  },
  benefitsHalf: {
    textAlign: "left",
    padding: "40px",
    borderRadius: "16px",
    border: "1px solid rgba(0, 0, 0, 0.08)"
  },
  benefitsContent: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "80px",
    alignItems: "center",
    maxWidth: "1200px",
    margin: "0 auto"
  },
  benefitsText: {
    textAlign: "left"
  },
  benefitsTitle: {
    fontSize: "48px",
    fontWeight: "700",
    lineHeight: "1.1",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "24px",
    letterSpacing: "-0.03em"
  },
  benefitsTitleAlt: {
    fontSize: "48px",
    fontWeight: "700",
    lineHeight: "1.1",
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "24px",
    letterSpacing: "-0.03em"
  },
  benefitsDescription: {
    fontSize: "17px",
    lineHeight: "1.7",
    color: "#4a5568",
    marginBottom: "32px",
    fontWeight: "400"
  },
  checkList: {
    display: "flex",
    flexDirection: "column",
    gap: "20px"
  },
  checkItem: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    fontSize: "17px",
    color: "#2d3748",
    fontWeight: "500"
  },
  docLink: {
    color: "#2d3748",
    fontWeight: "500",
    textDecoration: "underline",
    textDecorationColor: "rgba(245, 87, 108, 0.5)",
    textUnderlineOffset: "3px",
    transition: "color 0.2s ease"
  },
  benefitsStats: {
    display: "flex",
    flexDirection: "column",
    gap: "60px"
  },
  bigStat: {
    textAlign: "left",
    padding: "32px",
    background: "#ffffff",
    borderRadius: "16px",
    border: "1px solid rgba(0, 0, 0, 0.1)"
  },
  bigStatNumber: {
    fontSize: "72px",
    fontWeight: "700",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "12px",
    letterSpacing: "-0.03em",
    lineHeight: "1"
  },
  bigStatLabel: {
    fontSize: "18px",
    color: "#4a5568",
    fontWeight: "500"
  },
  
  // CTA
  finalCta: {
    padding: "120px 20px",
    background: "#ffffff",
    borderTop: "1px solid rgba(0, 0, 0, 0.08)"
  },
  ctaContent: {
    textAlign: "center",
    maxWidth: "700px",
    margin: "0 auto"
  },
  ctaTitle: {
    fontSize: "52px",
    fontWeight: "700",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "24px",
    letterSpacing: "-0.02em"
  },
  ctaSubtitle: {
    fontSize: "21px",
    lineHeight: "1.6",
    color: "#4a5568",
    marginBottom: "48px",
    fontWeight: "400"
  },
  ctaButton: {
    fontSize: "18px",
    padding: "18px 40px"
  },
  ctaNote: {
    fontSize: "16px",
    color: "#718096",
    marginTop: "24px",
    fontWeight: "400"
  }
};

// Responsive styles - Enhanced
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  /* Tablet and below */
  @media (max-width: 1200px) {
    div.heroGrid {
      gap: 50px !important;
    }
  }

  @media (max-width: 1024px) {
    /* Hero Section */
    div.heroGrid {
      grid-template-columns: 1fr !important;
      gap: 40px !important;
    }
    
    div.heroImageSide {
      order: 1 !important;
      padding: 0 20px;
    }
    
    div.heroTextSide {
      order: 2 !important;
      text-align: center !important;
      padding: 0 20px;
    }
    
    div.heroCardImage {
      max-width: 100% !important;
    }
    
    h1.mainTitle {
      font-size: 48px !important;
      text-align: center !important;
    }
    
    p.mainSubtitle {
      text-align: center !important;
    }
    
    div.primaryCta {
      margin: 0 auto 50px !important;
    }
    
    div.heroStats {
      grid-template-columns: repeat(3, 1fr) !important;
      gap: 20px !important;
      text-align: center !important;
    }
    
    div.statItem {
      text-align: center !important;
    }
    
    /* Features Section */
    h2.featuresSectionTitle {
      font-size: 42px !important;
    }
    
    div.featuresGrid {
      grid-template-columns: 1fr !important;
      gap: 32px !important;
    }
    
    /* Full Stack Section */
    div.fullStackGrid {
      grid-template-columns: 1fr !important;
      gap: 24px !important;
    }
    
    h2.fullStackTitle {
      font-size: 36px !important;
    }

    /* Stack accordion */
    div.stackList {
      max-width: 100% !important;
    }
    button[data-testid^="stack-item-toggle"] span {
      font-size: 19px !important;
    }
    
    /* Benefits Section */
    div.benefitsTwoColumn {
      grid-template-columns: 1fr !important;
      gap: 32px !important;
    }
    
    h1.benefitsTitle {
      font-size: 48px !important;
    }
    
    div.benefitsContent {
      grid-template-columns: 1fr !important;
      gap: 50px !important;
    }
    
    div.benefitsText {
      text-align: center !important;
    }
    
    div.checkList {
      align-items: center !important;
    }
    
    /* CTA Section */
    h2.ctaTitle {
      font-size: 48px !important;
    }
  }
  
  /* Mobile */
  @media (max-width: 768px) {
    /* Hero Section Mobile */
    section.heroSection {
      padding: 100px 16px 80px !important;
    }
    
    div.heroImageSide {
      padding: 0 !important;
    }
    
    div.heroTextSide {
      padding: 0 !important;
    }
    
    h1.mainTitle {
      font-size: 36px !important;
      line-height: 1.2 !important;
      margin-bottom: 20px !important;
    }
    
    p.mainSubtitle {
      font-size: 17px !important;
      margin-bottom: 32px !important;
      line-height: 1.5 !important;
    }
    
    button.btn-primary {
      width: 100%;
      padding: 16px 32px !important;
      font-size: 16px !important;
    }
    
    div.primaryCta {
      margin-bottom: 40px !important;
    }
    
    div.heroStats {
      grid-template-columns: 1fr !important;
      gap: 24px !important;
      padding-top: 32px !important;
    }
    
    div.statNumber {
      font-size: 24px !important;
    }
    
    div.statLabel {
      font-size: 13px !important;
    }
    
    /* Features Section Mobile */
    section.featuresSection {
      padding: 80px 16px !important;
    }
    
    h2.featuresSectionTitle {
      font-size: 32px !important;
      margin-bottom: 50px !important;
      line-height: 1.2 !important;
    }
    
    div.featuresGrid {
      gap: 24px !important;
    }
    
    div.featureCard {
      padding: 28px 24px !important;
    }
    
    div.featureTitle {
      font-size: 22px !important;
      margin-bottom: 12px !important;
    }
    
    div.featureDescription {
      font-size: 16px !important;
      line-height: 1.6 !important;
    }

    /* Full Stack Section Mobile */
    section.fullStackSection {
      padding: 80px 16px !important;
    }
    h2.fullStackTitle {
      font-size: 32px !important;
      margin-bottom: 16px !important;
      line-height: 1.2 !important;
    }
    p.fullStackSubtitle {
      font-size: 16px !important;
      margin-bottom: 40px !important;
    }
    button[data-testid^="stack-item-toggle"] {
      padding: 22px 4px !important;
      gap: 12px !important;
    }
    button[data-testid^="stack-item-toggle"] span {
      font-size: 17px !important;
      line-height: 1.3 !important;
    }
    button[data-testid^="stack-item-toggle"] svg {
      width: 22px !important;
      height: 22px !important;
    }
    div[data-testid^="stack-item-"] > div:not([data-testid]) {
      padding-left: 4px !important;
      padding-right: 24px !important;
    }
    div[data-testid^="stack-item-"] p {
      font-size: 15px !important;
      line-height: 1.6 !important;
    }
    
    /* Benefits Section Mobile */
    section.benefitsSection {
      padding: 80px 16px !important;
    }
    
    h1.benefitsTitle {
      font-size: 36px !important;
      line-height: 1.2 !important;
      margin-bottom: 24px !important;
      text-align: center !important;
    }
    
    div.benefitsDescription {
      font-size: 16px !important;
      margin-bottom: 32px !important;
      text-align: center !important;
    }
    
    div.checkItem {
      font-size: 16px !important;
      justify-content: center !important;
    }
    
    div.bigStatNumber {
      font-size: 48px !important;
    }
    
    div.bigStatLabel {
      font-size: 16px !important;
    }
    
    div.bigStat {
      padding: 24px !important;
      text-align: center !important;
    }
    
    /* CTA Section Mobile */
    section.finalCta {
      padding: 80px 16px !important;
    }
    
    h2.ctaTitle {
      font-size: 36px !important;
      line-height: 1.2 !important;
      margin-bottom: 20px !important;
    }
    
    div.ctaSubtitle {
      font-size: 17px !important;
      margin-bottom: 32px !important;
    }
    
    div.ctaButton {
      width: 100%;
    }
  }
  
  /* Small Mobile */
  @media (max-width: 480px) {
    section.heroSection {
      padding: 80px 16px 60px !important;
    }
    
    h1.mainTitle {
      font-size: 28px !important;
    }
    
    p.mainSubtitle {
      font-size: 15px !important;
    }
    
    button.btn-primary,
    button.btn-secondary {
      padding: 14px 24px !important;
      font-size: 15px !important;
    }
    
    div.featureCard {
      padding: 20px !important;
    }
    
    h2.featuresSectionTitle {
      font-size: 28px !important;
    }
    
    div.featureTitle {
      font-size: 20px !important;
    }
    
    h1.benefitsTitle,
    h2.ctaTitle {
      font-size: 28px !important;
    }

    /* Full Stack Section small mobile */
    h2.fullStackTitle {
      font-size: 26px !important;
    }
    button[data-testid^="stack-item-toggle"] {
      padding: 18px 2px !important;
    }
    button[data-testid^="stack-item-toggle"] span {
      font-size: 17px !important;
    }
    div[data-testid^="stack-item-"] p {
      font-size: 14px !important;
    }
  }
  
`;
document.head.appendChild(styleSheet);

export default HomePage;
